/**
 * Athletics Enrichment Entity Schemas
 *
 * The shape the calendar response attaches as the optional `athletics` field on
 * an athletics event, joined server-side from `AthleticGame` by
 * `event_id` (plan 2026-06-14-001). The live per-user calendar remains the
 * visibility authority; this object only ADDS rich fields (structured address,
 * opponents, home/away, directions) that the admin-gated athletics endpoint
 * carries but the per-user calendar does not.
 *
 * Coverage is school-dependent — some schools fill in rich BB athletics data,
 * some none — so every field is optional/nullable and consumers must degrade
 * gracefully when a field is absent.
 *
 * See: docs/plans/2026-06-14-001-feat-athletics-event-enrichment-plan.md
 */

import { z } from 'zod'

/** One opponent on a game. Tournaments/invitationals carry several. */
export const AthleticOpponentSchema = z.object({
  id: z.number().nullable().optional(),
  name: z.string().nullable().optional(),
  /** Final score, free-text as BB returns it (e.g. "2-1"). */
  score: z.string().nullable().optional(),
  /** Outcome, free-text as BB returns it (e.g. "W"/"L"). */
  winLoss: z.string().nullable().optional()
})

export type AthleticOpponent = z.infer<typeof AthleticOpponentSchema>

/** Structured address block (all parts optional — BB coverage varies). */
export const AthleticAddressSchema = z.object({
  line1: z.string().nullable().optional(),
  line2: z.string().nullable().optional(),
  line3: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  zip: z.string().nullable().optional()
})

export type AthleticAddress = z.infer<typeof AthleticAddressSchema>

/**
 * Athletics enrichment object. Attached as `athletics?` on a calendar event when
 * an `AthleticGame` row matched the event's `event_id`. All fields are
 * optional; an empty `{}` is valid (a matched row with no rich data filled in).
 */
export const AthleticEnrichmentSchema = z.object({
  opponents: z.array(AthleticOpponentSchema).optional(),
  homeAway: z.string().nullable().optional(),
  locationName: z.string().nullable().optional(),
  address: AthleticAddressSchema.nullable().optional(),
  /** Precomputed clean maps query string the OS maps app can consume directly. */
  mapsQuery: z.string().nullable().optional(),
  /** HTML-sanitized directions text. */
  directions: z.string().nullable().optional(),
  cancelled: z.boolean().nullable().optional(),
  rescheduled: z.boolean().nullable().optional(),
  rescheduledNote: z.string().nullable().optional(),
  practice: z.boolean().nullable().optional(),
  /**
   * Sport label, joined from the game's linked athletics `Section.sportName`
   * (canonical athletics model, plan 2026-06-14-004). Null when the game has no
   * linked section or the section has no sport label. School-dependent coverage.
   */
  sport: z.string().nullable().optional()
})

export type AthleticEnrichment = z.infer<typeof AthleticEnrichmentSchema>
